import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGuardedNavigate } from "../context/AuthModalContext";
import { useTranslation } from "../context/TranslationContext";
import { getPublishedNews, getPublishedTutorials, getSiteContent } from "../api/content";
import CardMedia from "../components/discover/CardMedia";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";

function videoEmbedUrl(url) {
  if (!url) return null;
  const u = String(url).trim();
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  if (u.includes("youtube.com/embed")) return u.split("?")[0];
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return u;
}

const sortByOrder = (arr) =>
  [...(arr || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const ResourcesPage = ({ setToast }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const [news, setNews] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  const goCreateCampaign = () => navigate("/create-business");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [newsRes, tutRes, siteRes] = await Promise.all([
          getPublishedNews(80),
          getPublishedTutorials(),
          getSiteContent(),
        ]);
        if (cancelled) return;
        if (newsRes.success) setNews(newsRes.data || []);
        if (tutRes.success) setTutorials(tutRes.data || []);
        if (siteRes.success) setSite(siteRes.data || null);
      } catch (e) {
        if (!cancelled) setToast?.(e?.response?.data?.message || t("resources.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setToast, t]);

  const heroTitle = site?.heroTitle?.trim() || t("resources.heroTitle");
  const heroSubtitle = site?.heroSubtitle?.trim() || t("resources.heroSubtitle");
  const faqs = sortByOrder(site?.faqs);

  const sectionTitle = (title, subtitle) => (
    <div className="flex flex-col gap-space-4">
      <h2 className="text-title-3 text-label-primary">{title}</h2>
      <p className="text-footnote text-label-secondary">{subtitle}</p>
    </div>
  );

  return (
    <Page>
      <PageHeader
        eyebrow={t("resources.heroLabel")}
        title={heroTitle}
        subtitle={heroSubtitle}
        action={
          <Button size="m" onClick={() => navigate("/resources/about")}>
            {t("resources.aboutLink")}
          </Button>
        }
      />

      <section className="flex flex-col items-start gap-space-8 rounded-radius-lg bg-accent-tinted p-space-24">
        <p className="text-caps-label uppercase text-accent-text">{t("resources.heroCtaEyebrow")}</p>
        <p className="text-title-2 text-label-primary">{t("resources.heroCtaTitle")}</p>
        <p className="text-subheadline text-label-secondary">{t("resources.heroCtaSubtitle")}</p>
        <Button size="m" icon={<Icon name="plus" size={20} />} onClick={goCreateCampaign}>
          {site?.createMintSpaceButtonText || t("resources.createMintSpace")}
        </Button>
      </section>

      <section className="flex flex-col gap-space-24">
        {sectionTitle(t("resources.newsTitle"), t("resources.newsSubtitle"))}
        {loading ? (
          <p className="text-subheadline text-label-secondary">{t("common.loading")}</p>
        ) : news.length === 0 ? (
          <EmptyState icon="book" title={t("resources.noNews")} />
        ) : (
          <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 xl:grid-cols-3">
            {news.map((article) => (
              <Link
                key={article._id}
                to={`/resources/news/${encodeURIComponent(article.slug)}`}
                className="flex flex-col gap-space-12 overflow-hidden rounded-radius-lg bg-bg-raised outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
              >
                <CardMedia src={article.coverImageUrl} placeholderIcon="book" placeholderLabel={t("resources.articleLabel")} className="h-[180px]" />
                <div className="flex flex-col gap-space-8 p-space-16">
                  <h3 className="line-clamp-2 text-headline text-label-primary">{article.title}</h3>
                  {article.excerpt && <p className="line-clamp-2 text-footnote text-label-secondary">{article.excerpt}</p>}
                  {article.publishedAt && (
                    <p className="text-caption-1 text-label-tertiary">{new Date(article.publishedAt).toLocaleDateString()}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-space-24">
        {sectionTitle(t("resources.tutorialsTitle"), t("resources.tutorialsSubtitle"))}
        {loading ? null : tutorials.length === 0 ? (
          <EmptyState icon="play" title={t("resources.noTutorials")} />
        ) : (
          <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 xl:grid-cols-3">
            {tutorials.map((item) => {
              const u = item.videoUrl?.trim() || "";
              const isYtVm = /youtube\.com|youtu\.be|vimeo\.com/i.test(u);
              const iframeSrc = u && isYtVm ? videoEmbedUrl(u) : null;
              const useNativeVideo =
                u &&
                !isYtVm &&
                (/\.(mp4|webm|ogg)(\?|$)/i.test(u) || /backblazeb2\.com|b-cdn\.net/i.test(u));
              return (
                <div key={item._id} className="flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
                  <div className="relative h-[180px] w-full bg-fill-quaternary">
                    {useNativeVideo || (u && !isYtVm && !iframeSrc) ? (
                      <video title={item.title} src={u} className="h-full w-full object-contain" controls playsInline preload="metadata" />
                    ) : iframeSrc ? (
                      <iframe
                        title={item.title}
                        src={iframeSrc}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-label-tertiary">
                        <Icon name="play" size={32} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-space-8 p-space-16">
                    <h3 className="text-headline text-label-primary">{item.title}</h3>
                    {item.description && <p className="line-clamp-3 text-footnote text-label-secondary">{item.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {faqs.length > 0 && (
        <section className="flex flex-col gap-space-16">
          <h2 className="text-title-3 text-label-primary">{site?.faqTitle || t("resources.faqTitle")}</h2>
          <div className="flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-separator-default last:border-b-0">
                <button
                  type="button"
                  aria-expanded={openFaq === idx}
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="flex w-full items-center justify-between gap-space-12 px-space-16 py-space-12 text-left"
                >
                  <span className="text-headline text-label-primary">{faq.question}</span>
                  <Icon name={openFaq === idx ? "chevron-up" : "chevron-down"} size={20} className="shrink-0 text-label-tertiary" />
                </button>
                {openFaq === idx && <p className="px-space-16 pb-space-12 text-subheadline text-label-secondary">{faq.answer}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </Page>
  );
};

export default ResourcesPage;
